import { useState, useEffect, useCallback } from 'react'
import { useQuery } from '@tanstack/react-query'
import api from '../services/api'

const ICONS = { cnc: '🔧', zaag: '🪚', boor: '🔩', lak: '🎨', assemblage: '🏗️', pers: '⚙️' }
const STATUS = {
  draait: { kleur: '#2D6A4F', bg: '#E8F5E9', label: 'In productie' },
  vrij: { kleur: '#1A3A5C', bg: '#EFF3F8', label: 'Beschikbaar' },
  onderhoud: { kleur: '#E8830A', bg: '#FFF8E1', label: 'Onderhoud' },
  storing: { kleur: '#B02020', bg: '#FFEBEE', label: 'Storing' },
}

export default function DigitaleTweelingDashboard() {
  const [machines, setMachines] = useState([])
  const [live, setLive] = useState(false)
  const [lastUpdate, setLastUpdate] = useState(null)

  const { data: snapshot } = useQuery({
    queryKey: ['dt-snapshot'],
    queryFn: () => api.get('/digitale-tweeling/snapshot').then(r => r.data),
    staleTime: 10000,
  })

  useEffect(() => { if (snapshot && !machines.length) setMachines(snapshot) }, [snapshot])

  useEffect(() => {
    let ws
    try {
      ws = new WebSocket(`${window.location.protocol === 'https:' ? 'wss' : 'ws'}://${window.location.host}/api/v1/digitale-tweeling/ws`)
      ws.onopen = () => setLive(true)
      ws.onmessage = (e) => { try { const d = JSON.parse(e.data); if (d.type === 'machine_update') { setMachines(d.machines); setLastUpdate(new Date()) } } catch {} }
      ws.onclose = () => setLive(false)
    } catch {}
    return () => ws?.close()
  }, [])

  const stats = { totaal: machines.length, draaiend: machines.filter(m => m.status === 'draait').length, vrij: machines.filter(m => m.status === 'vrij').length, storing: machines.filter(m => m.status === 'storing').length }

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-5 flex-wrap gap-3">
        <h2 className="text-xl font-bold text-[#1A3A5C] m-0">🏭 Digitale Tweeling</h2>
        <div className="flex items-center gap-3">
          {lastUpdate && <span className="text-xs text-gray-400">Bijgewerkt {lastUpdate.toLocaleTimeString('nl-BE')}</span>}
          <span className={`px-3 py-1 rounded-full text-xs font-bold ${live ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-600'}`}>{live ? '● LIVE' : '○ Offline'}</span>
        </div>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
        {[['Totaal', stats.totaal, '#1A3A5C'], ['Actief', stats.draaiend, '#2D6A4F'], ['Vrij', stats.vrij, '#0B6E5E'], ['Storing', stats.storing, '#B02020']].map(([l, n, c]) => (
          <div key={l} className="bg-white rounded-xl p-4 text-center shadow-sm border">
            <div className="text-2xl font-bold" style={{ color: c }}>{n}</div>
            <div className="text-xs text-gray-500 mt-1">{l}</div>
          </div>
        ))}
      </div>
      {machines.length === 0 ? (
        <div className="bg-white rounded-xl p-12 text-center text-gray-400 border border-dashed">
          <div className="text-4xl mb-3">🏭</div><p>Geen machines geconfigureerd.</p>
        </div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
          {machines.map(m => {
            const cfg = STATUS[m.status] || STATUS.vrij
            return (
              <div key={m.id} className="rounded-xl p-5 text-center transition-all relative border-2" style={{ background: cfg.bg, borderColor: cfg.kleur }}>
                {m.status === 'draait' && <div className="absolute top-2.5 right-2.5 w-2.5 h-2.5 rounded-full bg-green-600" style={{ boxShadow: '0 0 0 3px rgba(45,106,79,0.3)' }} />}
                <div className="text-3xl mb-2">{ICONS[m.type] || '⚙️'}</div>
                <div className="font-bold text-sm text-[#1A3A5C] mb-2">{m.naam}</div>
                <span className="inline-block px-2.5 py-0.5 rounded-xl text-xs font-bold text-white" style={{ background: cfg.kleur }}>{cfg.label}</span>
                {m.actieve_order && <div className="mt-2 text-xs text-gray-600 bg-white rounded p-1">📋 {m.actieve_order}</div>}
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}
